import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { map } from 'rxjs';
import { UtilityHelper } from '../../../helpers/utility.helper';
import { OnyxStorageService } from '../../../services/onyx-storage.service';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxAvatarCompany, OnyxAvatarUser } from '../interfaces';

@Component({
  selector: 'onyx-avatar',
  imports: [OnyxIconComponent, AsyncPipe],
  templateUrl: './onyx-avatar.component.html',
  styleUrl: './onyx-avatar.component.scss',
})
export class OnyxAvatarComponent {
  private readonly storageService = inject(OnyxStorageService);

  public readonly variant = input.required<'user' | 'company'>();
  public readonly size = input.required<16 | 20 | 24 | 32 | 60>();
  public readonly user = input<OnyxAvatarUser | null>();
  public readonly company = input<OnyxAvatarCompany | null>();
  public readonly clickable = input(false);

  private readonly COLORS = [
    'green',
    'yellow',
    'red',
    'violet',
    'orange',
    'pink',
  ] as const;

  protected color = computed(() => {
    if (this.company()) return;

    const user = this.user();
    if (!user?.firstName || !user?.lastName) return 'disabled';

    const fullName =
      `${user.firstName.trim()}${user.lastName.trim()}`.toLowerCase();
    const hash = UtilityHelper.djb2Hash(fullName);

    return this.COLORS[hash % this.COLORS.length];
  });

  protected background$ = computed(() => {
    const backgroundUuid =
      this.variant() === 'user' ? this.user()?.avatar : this.company()?.logo;
    if (!backgroundUuid) return;

    return this.storageService.getFile(backgroundUuid).pipe(
      map((file) => URL.createObjectURL(file)),
      map((url) => `center / cover url(${url}), #fff`),
    );
  });

  protected initials = computed(() => {
    const user = this.user();
    if (!user?.firstName || !user?.lastName) return;

    const initials = `${user.firstName.trimStart().charAt(0)}${![16, 20].includes(this.size()) ? user.lastName.trimStart().charAt(0) : ''}`;
    return initials.toUpperCase();
  });

  protected companyIconSize = computed(
    () => ({ 60: 28, 32: 16, 24: 12, 20: 10, 16: 8 })[this.size()],
  );
}
